'use client'

import { IconInfoCircle } from '@macarongfactory/web-icons'
import { Text, TextButton } from '@macarongfactory/web-ui'
import { motion } from 'motion/react'
import { useState } from 'react'

import { IconAiSparkle } from '@/shared/assets/icons'

import {
  MODOOCAR_CODE,
  splitAiSummaryForLock,
  useModoocarTracking,
} from '../_features/modoocar'

interface IAiSummaryCardProps {
  summary: string
  basis: string
  hasData?: boolean
  /** [정책]: 잠금 화면(B)에서도 텍스트는 HTML에 그대로 두고 화면에서만 흐리게 가립니다. 크롤러는 A안과 같은 내용을 읽습니다. */
  locked?: boolean
}

const TOOLTIP_TEXT =
  '마이클 오너들의 마이클 실사용 데이터를 바탕으로 요약한 내용입니다.'

const NO_DATA_TITLE = '마이클 실사용 데이터 수집중'
const NO_DATA_BODY = '아직 마이클 실사용 리뷰가 없어요.\n조금만 기다려 주세요!'

const COLLAPSED_HEIGHT = 30

const AiSummaryCard = ({
  summary,
  basis,
  hasData = true,
  locked = false,
}: IAiSummaryCardProps) => {
  const [expanded, setExpanded] = useState(false)
  const [tooltipOpen, setTooltipOpen] = useState(false)
  const { itemClick } = useModoocarTracking()

  const handleExpand = () => {
    itemClick(MODOOCAR_CODE.MODEL_INFO, 'accordion', '마이클 실사용 분석', {
      status: 'on',
    })
    setExpanded(true)
  }
  const handleCollapse = () => {
    itemClick(MODOOCAR_CODE.MODEL_INFO, 'accordion', '마이클 실사용 분석', {
      status: 'off',
    })
    setExpanded(false)
  }

  if (!hasData) {
    return (
      <div className='flex flex-col gap-dimension-x1 rounded-dimension-x4 border border-stroke-neutral-strong bg-background-surface-default px-dimension-x4 py-dimension-x3'>
        <div className='flex items-center gap-dimension-x1'>
          <IconAiSparkle width={24} height={24} />
          <Text variant='title-2-semibold' color='text-foreground-brand-strong'>
            {NO_DATA_TITLE}
          </Text>
        </div>
        <Text
          variant='title-2-medium'
          color='text-foreground-neutral-alternative'
          className='whitespace-pre-line break-keep'
        >
          {NO_DATA_BODY}
        </Text>
      </div>
    )
  }

  return (
    <div className='flex flex-col gap-dimension-x1 rounded-dimension-x4 border border-stroke-neutral-strong bg-background-surface-default px-dimension-x4 py-dimension-x3'>
      <div className='flex items-center gap-dimension-x1'>
        <IconAiSparkle width={24} height={24} />
        <Text variant='title-2-semibold' color='text-foreground-brand-strong'>
          마이클 실사용 분석
        </Text>
        <div className='relative inline-flex'>
          <button
            type='button'
            aria-label='실사용 분석 설명'
            aria-expanded={tooltipOpen}
            onClick={() => setTooltipOpen(prev => !prev)}
            className='inline-flex text-foreground-neutral-assistive'
          >
            <IconInfoCircle width={18} height={18} />
          </button>
          {tooltipOpen ? (
            <div
              role='tooltip'
              className='absolute bottom-[calc(100%+6px)] left-1/2 z-10 w-[240px] -translate-x-1/2'
            >
              <div className='rounded-dimension-x1 bg-background-neutral-inverse px-dimension-x3 py-dimension-x2'>
                <Text
                  variant='body-2-regular'
                  color='text-foreground-neutral-inverse'
                  className='break-keep'
                >
                  {TOOLTIP_TEXT}
                </Text>
              </div>
              <span
                aria-hidden
                className='absolute left-1/2 top-full h-2 w-2 -translate-x-1/2 -translate-y-1/2 rotate-45 bg-background-neutral-inverse'
              />
            </div>
          ) : null}
        </div>
      </div>

      <motion.div
        className='flex flex-col gap-dimension-x1 overflow-hidden'
        initial={false}
        animate={{ height: expanded ? 'auto' : COLLAPSED_HEIGHT }}
        transition={{ duration: 0.25, ease: 'easeInOut' }}
      >
        <div className='flex items-center gap-dimension-x1'>
          <Text
            variant='title-2-medium'
            color='text-foreground-neutral-strong'
            className={
              expanded ? 'w-full break-keep' : 'min-w-0 flex-1 truncate'
            }
          >
            {locked
              ? splitAiSummaryForLock(summary).map((segment, index) =>
                  segment.blur ? (
                    <span
                      key={index}
                      className='select-none text-foreground-neutral-assistive blur-[5px]'
                      aria-hidden
                    >
                      {segment.text}
                    </span>
                  ) : (
                    <span key={index}>{segment.text}</span>
                  )
                )
              : summary}
          </Text>
          {expanded ? null : (
            <TextButton
              text='더보기'
              variant='underline'
              typography='body-1-semibold'
              textColor='foreground-neutral-alternative'
              clickable={handleExpand}
              className='shrink-0'
            />
          )}
        </div>

        <div className='flex items-center justify-between gap-dimension-x2'>
          <Text
            variant='body-2-medium'
            color='text-foreground-neutral-alternative'
          >
            {basis} 마이클 데이터 기준
          </Text>
          <TextButton
            text='닫기'
            variant='underline'
            typography='body-1-semibold'
            textColor='foreground-neutral-alternative'
            clickable={handleCollapse}
            className='shrink-0'
          />
        </div>
      </motion.div>
    </div>
  )
}

export default AiSummaryCard
